import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import Switch from "react-switch";
import StageTabs, { useAdminTheme } from "../../components/StageTabs";
import { ensureThemeJsonArray, isTrue } from "../../../functions/themeAssets";

const MAX_RULES = 6;
const MAX_RULE_LEN = 200;
const MAX_THANKYOU_LEN = 300;
const MAX_TAB_NAME_LEN = 80;

/** <input type="color"> only accepts #rrggbb */
function toColor(val, fallback) {
  const v = String(val || "").trim();
  if (/^#[0-9a-fA-F]{6}$/.test(v)) return v.toLowerCase();
  if (/^#[0-9a-fA-F]{3}$/.test(v)) return `#${v[1]}${v[1]}${v[2]}${v[2]}${v[3]}${v[3]}`.toLowerCase();
  const probe = document.createElement("canvas").getContext("2d");
  probe.fillStyle = "#000000";
  probe.fillStyle = v; // named colours ("Black", "white") come back as #rrggbb
  return /^#[0-9a-f]{6}$/i.test(probe.fillStyle) ? probe.fillStyle : fallback;
}

/** Home — thank-you text, rules and colours (themeUpdate.php). */
export default function Rules() {
  const { data, status, save } = useAdminTheme();
  const [tabName, setTabName] = useState("");
  const [thankYou, setThankYou] = useState("");
  const [rules, setRules] = useState([""]);
  const [textColor, setTextColor] = useState("#000000");
  const [buttonColor, setButtonColor] = useState("#ffffff");
  const [buttonBg, setButtonBg] = useState("#000000");
  const [leaderboard, setLeaderboard] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!data) return;
    const r = ensureThemeJsonArray(data.rules).map(String);
    setRules(r.length ? r : [""]);
    setTabName(String(data.tab_name ?? "Big Picture"));
    setThankYou(String(data.custom_text_thank_you_page ?? ""));
    setLeaderboard(isTrue(data.leaderboard_toggle));
    setTextColor(toColor(data.landing_page_title_color, "#000000"));
    setButtonColor(toColor(data.landing_page_button_color, "#ffffff"));
    setButtonBg(toColor(data.landing_page_button_bgcolor, "#000000"));
  }, [data]);

  const setRule = (i, v) => setRules(rules.map((r, idx) => (idx === i ? v : r)));

  const addRule = () => {
    if (rules.length >= MAX_RULES) return Swal.fire("Limit", `Keep ${MAX_RULES} rules at the most.`, "warning");
    setRules([...rules, ""]);
  };

  const removeRule = (i) => setRules(rules.filter((_, idx) => idx !== i));

  const onSave = async () => {
    const list = rules.map((r) => r.trim()).filter(Boolean);
    let problem = "";
    if (tabName.trim().length === 0) problem = "The tab name cannot be empty.";
    else if (tabName.length > MAX_TAB_NAME_LEN) problem = `The tab name can be at most ${MAX_TAB_NAME_LEN} characters.`;
    else if (list.length === 0) problem = "at least 1 rule required.";
    else if (list.some((r) => r.length > MAX_RULE_LEN)) problem = "Rules must below 200 characters.";
    else if (thankYou.trim().length === 0) problem = "The Thankyou Text cannot be empty or contain only spaces.";
    else if (thankYou.length > MAX_THANKYOU_LEN) problem = "You exceed the maximum limit of the Thankyou page text.";
    if (problem) return Swal.fire(problem, "", "error");

    setSaving(true);
    try {
      await save({
        rules: list,
        tab_name: tabName.trim(),
        landing_page_title_color: textColor,
        landing_page_button_color: buttonColor,
        landing_page_button_bgcolor: buttonBg,
        custom_text_thank_you_page: thankYou,
        leaderboard_toggle: leaderboard,
      });
      Swal.fire({ title: "Data Updated", icon: "success", timer: 1000, showConfirmButton: false });
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save", "error");
    } finally {
      setSaving(false);
    }
  };

  if (status === "loading" && !data) return <div className="themeupdate-loading">Loading...</div>;

  return (
    <>
      <StageTabs />
      <div className="bp-card">
        <h4>Tab Name:</h4>
        <input
          type="text"
          className="bp-input"
          maxLength={MAX_TAB_NAME_LEN}
          value={tabName}
          onChange={(e) => setTabName(e.target.value)}
        />
        <p className="bp-note">* This name is shown on the browser tab of the game &nbsp; * Maximum character limit is 80 characters</p>

        <h4>Add Custom thankyou text:</h4>
        <textarea
          className="bp-textarea"
          maxLength={MAX_THANKYOU_LEN}
          value={thankYou}
          onChange={(e) => setThankYou(e.target.value)}
        />
        <p className="bp-note">* This text will reflect on the thankyou page &nbsp; * character limit of text is 300</p>

        <h4>Leaderboard:</h4>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Switch checked={leaderboard} onChange={setLeaderboard} onColor="#4caf50" />
          <span>{leaderboard ? "On" : "Off"}</span>
        </div>
        <p className="bp-note">
          * When on, the thank you page shows the top 5 players below the big picture (and the player's own rank if
          they are not in the top 5)
        </p>

        <h4>Add Custom Rules:</h4>
        {rules.map((rule, i) => (
          <div className="bp-rule-row" key={i}>
            <textarea
              className="bp-textarea"
              placeholder="Rules"
              value={rule}
              onChange={(e) => setRule(i, e.target.value)}
            />
            <button type="button" className="bp-btn danger" onClick={() => removeRule(i)}>
              <i className="fa-solid fa-xmark" />
            </button>
          </div>
        ))}
        <button type="button" className="bp-btn" onClick={addRule}>
          <i className="fa-solid fa-plus" /> Add new rule
        </button>
        <p className="bp-note">
          * Keep 6 rules at the most &nbsp; * At least 1 rule is required &nbsp; * character limit of each rule is 200
          &nbsp; * Use || to split a rule: the part after it is highlighted on a second line
        </p>

        <div className="bp-colors">
          <label>
            Text Color
            <input type="color" value={textColor} onChange={(e) => setTextColor(e.target.value)} />
          </label>
          <label>
            Button Text Color
            <input type="color" value={buttonColor} onChange={(e) => setButtonColor(e.target.value)} />
          </label>
          <label>
            Button Background Color
            <input type="color" value={buttonBg} onChange={(e) => setButtonBg(e.target.value)} />
          </label>
        </div>

        <div className="bp-actions">
          <button type="button" className="bp-btn" onClick={onSave} disabled={saving}>
            Save
          </button>
        </div>
      </div>
    </>
  );
}
